import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { ArrowDownAZ, ArrowDownUp, ArrowUpZA } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import MonthPicker from "../../components/MonthPicker";
import { Page } from "../../components/Page";
import { ShowToast } from "../../components/toast";
// Local imports
import { api } from "../../helpers/api";
import {
  BillingFilters,
  BulkChargeModal,
  ChargePreviewModal,
  ChargeTotpModal,
  ClientDetailModal,
  ConfirmModal,
  EnrollTotpModal,
  InvoiceEmailPreviewModal,
  LinkStripeCustomerModal,
  PromoValidatorModal,
  SummaryStats,
  SyncAllModal,
  TableSkeleton,
  useBillingColumns,
} from "./components";
import { useChargeUnlock } from "./hooks/useChargeUnlock";
import { UnlockCountdown } from "./components/UnlockCountdown";
import {
  fetchChargePreview,
  useAllStripePlans,
  useBillingEnrichment,
  useBillingList,
  useBillingSummary,
  useBulkChargeStatus,
  useChargeBulk,
  useChargeClient,
  useLinkStripeCustomer,
  useReconcileAll,
  useRetryCharge,
  useScheduleInvoiceNotice,
  useSendInvoiceEmail,
  useSetCommissionRate,
  useSyncPaymentMethod,
  useVoidInvoice,
} from "./hooks";
import type { ChargeConfirmPayload } from "./components/ChargePreviewModal";
import {
  BillingFilters as BillingFiltersType,
  ClientBilling,
  CollectionMethod,
  PlanInfoMap,
} from "./types";
import {
  computeSummaryStats,
  exportBillingCsv,
  fetchAllBillingData,
  filterClients,
  formatCurrencyPrecise,
  mergeEnrichmentData,
  mergePlanInfo,
  sortBillingData,
  transformBillingData,
  transformPreviewData,
} from "./utils";

// =============================================================================
// MAIN COMPONENT
// =============================================================================

// The list is fetched whole and filtered client-side, so these never change.
// ponytail: constants, not state — nothing sets them.
const PAGE = 1;
const LIMIT = 10000;

const displayNameOf = (c: ClientBilling) =>
  c.clientName || c.hotelName || c.email;

/** The subset of the raw billing DTO the enrichment scope reads. */
type BillingListItem = {
  userId: string;
  enrichmentPending?: boolean;
  isTestUser?: boolean;
};

export function Billing() {
  const [selectedMonth, setSelectedMonth] = useState(
    moment().format("YYYY-MM"),
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showInvoiceEmailPreviewModal, setShowInvoiceEmailPreviewModal] =
    useState(false);
  // The billing choice captured when the admin opens the email preview, so the
  // projected preview matches what a "Send Invoice" would actually deliver.
  const [previewEmailOpts, setPreviewEmailOpts] = useState<{
    collectionMethod?: CollectionMethod;
    daysUntilDue?: number;
  }>({});
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewClient, setPreviewClient] = useState<ClientBilling | null>(
    null,
  );
  const [loadingPreviewForClientId, setLoadingPreviewForClientId] = useState<
    string | null
  >(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Filter state
  const [filters, setFilters] = useState<BillingFiltersType>({
    searchQuery: "",
    statusFilter: "all",
    showReadyOnly: false,
    showTestUsers: false,
  });
  const patchFilters = (patch: Partial<BillingFiltersType>) =>
    setFilters((f) => ({ ...f, ...patch }));
  // API hooks
  const { data: billingData, isLoading: isBillingLoading } = useBillingList(
    selectedMonth,
    PAGE,
    LIMIT,
  );
  const { data: summaryData } = useBillingSummary(selectedMonth);
  const { data: allStripePlans } = useAllStripePlans();
  const chargeClientMutation = useChargeClient((data, variables) => {
    setShowInvoiceEmailPreviewModal(false);
    // send_invoice: keep the charge modal open and flip it to the issued
    // "Invoice Details" view so the admin can copy the pay link right here.
    // Cherry-pick only the two fields off the raw CommissionCharge response —
    // spreading the whole entity would corrupt the ClientBilling shape (its
    // `status`/decimal totals differ from the mapped preview).
    if (variables.collectionMethod === "send_invoice") {
      setPreviewClient((prev) =>
        prev
          ? {
              ...prev,
              invoiceStatus: "invoiced",
              stripeHostedInvoiceUrl: data.stripeHostedInvoiceUrl ?? null,
            }
          : prev,
      );
      return;
    }
    setShowPreviewModal(false);
    setPreviewClient(null);
  });
  // Emailed-invoice billing: the notice goes out and the charge is booked, so
  // the period is settled as far as this modal is concerned — close it.
  const scheduleInvoiceNoticeMutation = useScheduleInvoiceNotice(() => {
    setShowInvoiceEmailPreviewModal(false);
    setShowPreviewModal(false);
    setPreviewClient(null);
  });
  const retryChargeMutation = useRetryCharge();
  // Retry re-collects the ORIGINAL invoice amount — confirm it (showing the
  // amount) before charging, with an escape hatch to Adjust for a new amount.
  const [retryConfirmClient, setRetryConfirmClient] =
    useState<ClientBilling | null>(null);
  const [rechargeClient, setRechargeClient] = useState<ClientBilling | null>(
    null,
  );
  const voidInvoiceMutation = useVoidInvoice();
  const sendInvoiceEmailMutation = useSendInvoiceEmail();
  const [voidInvoiceClient, setVoidInvoiceClient] =
    useState<ClientBilling | null>(null);
  // True while the open charge preview is a re-charge of an existing (often
  // already-settled) period. Threaded to the charge call as acknowledgeRecharge
  // so the backend overwrites the existing charge in place — no pre-cancel, so a
  // settled charge stays "charged" if the admin abandons the preview.
  const [isRecharge, setIsRecharge] = useState(false);
  const syncPaymentMethodMutation = useSyncPaymentMethod();
  const linkStripeMutation = useLinkStripeCustomer();
  const [linkStripeClient, setLinkStripeClient] =
    useState<ClientBilling | null>(null);
  const [detailClientId, setDetailClientId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPromoValidator, setShowPromoValidator] = useState(false);
  const setRateMutation = useSetCommissionRate();

  // Bulk Stripe reconciliation ("Sync with Stripe") state
  const reconcileAllMutation = useReconcileAll();
  const [showSyncAll, setShowSyncAll] = useState(false);
  const handleSyncAll = () =>
    reconcileAllMutation.mutate(undefined, {
      onSuccess: () => setShowSyncAll(true),
    });

  // Bulk charge state
  const [showBulkChargeConfirm, setShowBulkChargeConfirm] = useState(false);
  const [bulkJobId, setBulkJobId] = useState<string | null>(null);
  const chargeBulkMutation = useChargeBulk();
  const { data: bulkJobData } = useBulkChargeStatus(bulkJobId);
  const bulkJob = bulkJobData?.data;

  // Step-up unlock gate for money-moving charge actions (see useChargeUnlock).
  const unlock = useChargeUnlock();
  const { runCharge, onChargeError } = unlock;

  // Enrich every pending row in ONE call per month (plus one more if test
  // accounts are toggled on). The search box is deliberately NOT part of this
  // scope: keying the enrich query on the search term fired a fresh /enrich
  // per distinct term and dropped already-enriched rows back to skeletons
  // whenever they left the result set. Search is purely client-side now.
  // (Status / ready-only filters depend on enriched fields, so they're left
  // out to avoid an enrich → canCharge → filter → enrich feedback loop.)
  const enrichUserIds = useMemo(() => {
    if (!billingData?.data) return [];
    return (billingData.data as BillingListItem[])
      .filter((item) => item.enrichmentPending)
      .filter((item) => filters.showTestUsers || !item.isTestUser)
      .map((item) => item.userId);
  }, [billingData, filters.showTestUsers]);

  const { data: enrichmentData } = useBillingEnrichment(
    enrichUserIds,
    selectedMonth,
    enrichUserIds.length > 0,
  );

  // Build userId -> plan info and stripeCustomerId -> plan info maps
  const planInfoMap: PlanInfoMap = useMemo(() => {
    const userIdMap = new Map<
      string,
      { planDescription: string; subscriptionStatus: string }
    >();
    const stripeIdMap = new Map<
      string,
      { planDescription: string; subscriptionStatus: string }
    >();
    if (!allStripePlans) return { userIdMap, stripeIdMap };
    for (const customer of allStripePlans) {
      const activeSub =
        customer.subscriptions.find(
          (s) => s.status === "active" || s.status === "trialing",
        ) || customer.subscriptions[0];
      if (!activeSub) continue;
      const info = {
        planDescription: activeSub.planDescription || "",
        subscriptionStatus: activeSub.status,
      };
      if (customer.userId) {
        userIdMap.set(customer.userId, info);
      }
      stripeIdMap.set(customer.id, info);
    }
    return { userIdMap, stripeIdMap };
  }, [allStripePlans]);

  // Derive tableData from billing + enrichment only (fast DB path, no Stripe plans)
  const tableData = useMemo(() => {
    if (!billingData?.data) return [];
    let transformedData = transformBillingData(billingData.data);
    if (enrichmentData) {
      transformedData = mergeEnrichmentData(transformedData, enrichmentData);
    }
    return sortBillingData(transformedData);
  }, [billingData, enrichmentData]);

  // Lazily merge plan info once Stripe plans resolve, then re-sort (plans affect sort order)
  const tableDataWithPlans = useMemo(() => {
    if (planInfoMap.userIdMap.size === 0 && planInfoMap.stripeIdMap.size === 0)
      return tableData;
    return sortBillingData(mergePlanInfo(tableData, planInfoMap));
  }, [tableData, planInfoMap]);

  const detailClient = useMemo(
    () => tableDataWithPlans.find((c) => c.userId === detailClientId) ?? null,
    [tableDataWithPlans, detailClientId],
  );

  const knownHotelNames = useMemo(
    () =>
      tableDataWithPlans
        .map((c) => c.hotelName)
        .filter((n): n is string => Boolean(n)),
    [tableDataWithPlans],
  );

  const isLoading = isBillingLoading;

  // Filtered data based on search, status, and ready-to-charge filters
  const filteredData = useMemo(
    () => filterClients(tableDataWithPlans, filters),
    [tableDataWithPlans, filters],
  );

  // Calculate summary stats
  // Use backend summary for totals, but effective status for pending/paid counts
  const summaryStats = useMemo(
    () => computeSummaryStats(tableData, summaryData, filters.showTestUsers),
    [tableData, summaryData, filters.showTestUsers],
  );

  // Selection handlers
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((c) => c.id)));
    }
  };

  // Charge card handlers
  const handleChargeCard = async (
    client: ClientBilling,
    isRechargeFlow = false,
  ) => {
    // Set fresh on every preview open (the only entry point), so a normal charge
    // never inherits a stale re-charge acknowledgment from a prior session.
    setIsRecharge(isRechargeFlow);
    setLoadingPreviewForClientId(client.id);
    setIsLoadingPreview(true);
    const [year, month] = selectedMonth.split("-");
    try {
      const previewData = await fetchChargePreview({
        userId: client.userId,
        month,
        year,
      });

      const transformedPreview = transformPreviewData(previewData, client);
      setPreviewClient(transformedPreview);
      setIsLoadingPreview(false);
      setShowPreviewModal(true);
    } catch (error: any) {
      setIsLoadingPreview(false);

      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to load charge preview",
      });
    } finally {
      setLoadingPreviewForClientId(null);
    }
  };

  // Charge Again: open the editable charge preview (status overridden to
  // "pending") as a re-charge. The existing charge is NOT cancelled up front —
  // the backend overwrites it in place only when a new charge actually goes
  // through (gated by acknowledgeRecharge), so a settled charge stays "charged"
  // if the admin reviews the preview and backs out.
  const handleConfirmChargeAgain = () => {
    if (!rechargeClient) return;
    const client = rechargeClient;
    setRechargeClient(null);
    handleChargeCard({ ...client, invoiceStatus: "pending" }, true);
  };

  const handleConfirmRetry = () => {
    if (!retryConfirmClient) return;
    const chargeId = retryConfirmClient.id;
    setRetryConfirmClient(null);
    runCharge(() =>
      retryChargeMutation.mutate(
        { chargeId },
        { onSuccess: () => unlock.done(), onError: onChargeError },
      ),
    );
  };

  const handleAdjustFromRetry = () => {
    if (!retryConfirmClient) return;
    const client = retryConfirmClient;
    setRetryConfirmClient(null);
    handleChargeCard(client);
  };

  const handleConfirmChargeCard = (payload: ChargeConfirmPayload) => {
    if (!previewClient) return;
    const [year, month] = selectedMonth.split("-").map(Number);
    const userId = previewClient.userId;

    // Emailed-invoice billing books the charge for later — a different endpoint,
    // behind the same unlock because it still commits to taking the money.
    if (payload.action === "schedule_notice") {
      runCharge(() =>
        scheduleInvoiceNoticeMutation.mutate(
          { userId, month, year, noticeDays: payload.noticeDays },
          { onSuccess: () => unlock.done(), onError: onChargeError },
        ),
      );
      return;
    }

    const { action, ...chargePayload } = payload;
    runCharge(() =>
      chargeClientMutation.mutate(
        {
          userId,
          month,
          year,
          ...chargePayload,
          acknowledgeRecharge: isRecharge,
        },
        {
          onSuccess: () => unlock.done(),
          onError: onChargeError,
        },
      ),
    );
  };

  const handleConfirmVoidInvoice = () => {
    if (!voidInvoiceClient) return;
    voidInvoiceMutation.mutate(voidInvoiceClient.id, {
      onSuccess: () => setVoidInvoiceClient(null),
    });
  };

  const handleConfirmBulkCharge = () => {
    setShowBulkChargeConfirm(false);
    const [yearStr, monthStr] = selectedMonth.split("-");
    const bulkUserIds = filteredData
      .filter((c) => selectedIds.has(c.id))
      .map((c) => c.userId);
    runCharge(() =>
      chargeBulkMutation.mutate(
        {
          userIds: bulkUserIds,
          month: Number(monthStr),
          year: Number(yearStr),
        },
        {
          onSuccess: (res) => {
            unlock.done();
            setBulkJobId(res.data.jobId);
          },
          onError: onChargeError,
        },
      ),
    );
  };

  const copyInvoicePayLink = async (client: ClientBilling) => {
    if (!client.stripeHostedInvoiceUrl) return;
    await navigator.clipboard.writeText(client.stripeHostedInvoiceUrl);
    ShowToast({ type: "success", message: "Payment link copied" });
  };

  const sendInvoiceEmailForClient = (client: ClientBilling) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    sendInvoiceEmailMutation.mutate({ userId: client.userId, month, year });
  };

  const syncPaymentMethod = (client: ClientBilling) => {
    syncPaymentMethodMutation.mutate(client.userId);
  };

  const toggleTestUser = (client: ClientBilling) => {
    const markingAsTest = !client.isTestUser;
    setRateMutation.mutate(
      {
        userId: client.userId,
        commissionRate: client.commissionRate / 100,
        billingEmail: client.stripeEmail || client.email || "",
        isTestUser: markingAsTest,
        _silentToast: true,
      },
      {
        onSuccess: () =>
          ShowToast({
            type: "success",
            message: markingAsTest
              ? "Marked as test user"
              : "Unmarked as test user",
          }),
      },
    );
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const allRawData = await fetchAllBillingData(
        selectedMonth,
        api.commissionBilling.getBillingList,
      );
      // Merge plan info using both userId and stripeCustomerId
      let allTransformed = mergePlanInfo(
        transformBillingData(allRawData),
        planInfoMap,
      );
      // Filter out test users from export
      allTransformed = allTransformed.filter((client) => !client.isTestUser);
      // Sort same as table: status → has plan → can charge → completeness → alpha
      allTransformed = sortBillingData(allTransformed);
      exportBillingCsv(allTransformed);
    } catch {
      ShowToast({ type: "error", message: "Failed to export CSV" });
    } finally {
      setIsExporting(false);
    }
  };

  // Table columns
  const columns = useBillingColumns({
    selectedIds,
    filteredRows: filteredData,
    selectedMonth,
    toggleSelect,
    toggleSelectAll,
    knownHotelNames,
    plansLoaded: Boolean(allStripePlans),
    loadingPreviewForClientId,
    isFailedActionPending:
      retryChargeMutation.isPending ||
      chargeClientMutation.isPending ||
      isLoadingPreview,
    retryingChargeId: retryChargeMutation.isPending
      ? retryChargeMutation.variables?.chargeId ?? null
      : null,
    onChargeCard: (c) => handleChargeCard(c),
    onRetry: setRetryConfirmClient,
    onRecharge: setRechargeClient,
    onSyncPaymentMethod: syncPaymentMethod,
    onLinkStripe: setLinkStripeClient,
    onCopyPayLink: copyInvoicePayLink,
    onSendInvoiceEmail: sendInvoiceEmailForClient,
    onVoidInvoice: setVoidInvoiceClient,
    onToggleTestUser: toggleTestUser,
  });

  const table = useReactTable<ClientBilling>({
    data: filteredData,
    columns,
    // Keep row identity tied to the client, not the array index: RateCell holds
    // local edit state per row, and a refetch that reorders filteredData would
    // otherwise carry it onto whichever client landed at that position.
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-surface-tertiary flex-1">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h1 className="text-2xl font-semibold text-content-primary">
            Billing
          </h1>
          <div className="flex items-center gap-3">
            {unlock.me?.authorized && unlock.me?.enrolled && (
              <UnlockCountdown
                unlockedUntil={unlock.unlockedUntil}
                onUnlock={unlock.promptUnlock}
              />
            )}
            <button
              onClick={() => setShowPromoValidator(true)}
              className="px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-surface-primary text-content-primary border-[#E9E9EB] hover:bg-surface-secondary"
            >
              Validate promo
            </button>
            <MonthPicker
              initialDate={selectedMonth}
              onChange={(month, year) => {
                if (month && year) {
                  setSelectedMonth(`${year}-${month}`);
                }
              }}
            />
          </div>
        </div>

        {/* Content */}
        <>
          <SummaryStats stats={summaryStats} />
          <BillingFilters
            filters={filters}
            onFiltersChange={patchFilters}
            sorting={sorting}
            onSortingChange={setSorting}
            selectedCount={selectedIds.size}
            onBulkCharge={() => setShowBulkChargeConfirm(true)}
            onExportCsv={handleExportCsv}
            isExporting={isExporting}
            onSyncAll={handleSyncAll}
            isSyncPending={reconcileAllMutation.isPending}
            isSyncDisabled={reconcileAllMutation.isPending || showSyncAll}
          />
          {/* Main Table */}
          <div className="bg-surface-primary rounded-lg shadow-sm border border-[#E9E9EB]">
            <div className="overflow-auto max-h-[calc(100vh-380px)]">
              <table className="w-full">
                <colgroup>
                  <col style={{ width: "40px" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => {
                        const canSort = header.column.getCanSort();
                        const sortDirection = header.column.getIsSorted();
                        return (
                          <th
                            key={header.id}
                            className="text-[0.875rem] font-medium text-[#202020] text-start p-0"
                          >
                            <div
                              className={clsx(
                                "w-full px-4 py-3 bg-[#F4F4F6] flex flex-row items-center gap-1 whitespace-nowrap",
                                index === headerGroup.headers.length - 1 &&
                                  "rounded-tr-lg",
                                index === 0 && "rounded-tl-lg",
                                canSort &&
                                  "cursor-pointer select-none hover:bg-[#EAEAEC]",
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                              {canSort && (
                                <span className="ml-1">
                                  {sortDirection === "asc" ? (
                                    <ArrowUpZA className="w-4 h-4 text-primary" />
                                  ) : sortDirection === "desc" ? (
                                    <ArrowDownAZ className="w-4 h-4 text-primary" />
                                  ) : (
                                    <ArrowDownUp className="w-4 h-4 text-gray-400" />
                                  )}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {isLoading ? (
                    <TableSkeleton rows={4} columns={12} />
                  ) : (
                    table.getRowModel().rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className="hover:bg-surface-secondary cursor-pointer"
                        onClick={() => setDetailClientId(row.original.userId)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={clsx(
                              "px-4 py-3 border-b border-b-[#E9E9EB] text-[0.8125rem]",
                              index === table.getRowModel().rows.length - 1 &&
                                "border-b-0",
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {!isLoading && filteredData.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="text-5xl mb-4">
                    {tableData.length === 0 ? "💰" : "🔍"}
                  </div>
                  <span className="text-content-tertiary text-base font-medium">
                    {tableData.length === 0
                      ? "No billing data found"
                      : "No results match your filters"}
                  </span>
                  <span className="text-gray-400 text-sm mt-1">
                    {tableData.length === 0
                      ? "Clients with Google Ads integration will appear here"
                      : "Try adjusting your search or filter criteria"}
                  </span>
                </div>
              )}
            </div>

            {/* Footer: total count */}
            {billingData && billingData.total > 0 && (
              <div className="px-4 py-3 border-t border-border-secondary">
                <div className="text-sm text-content-tertiary">
                  {filteredData.length} of {billingData.total} clients
                </div>
              </div>
            )}
          </div>
        </>

        {/* Client Detail Modal */}
        {detailClient && (
          <ClientDetailModal
            client={detailClient}
            onClose={() => setDetailClientId(null)}
          />
        )}

        {/* Sync with Stripe — bulk reconciliation results */}
        {showSyncAll && reconcileAllMutation.data && (
          <SyncAllModal
            data={reconcileAllMutation.data}
            onClose={() => {
              setShowSyncAll(false);
              reconcileAllMutation.reset();
            }}
          />
        )}

        {/* Validate promo code (CS helper) */}
        {showPromoValidator && (
          <PromoValidatorModal onClose={() => setShowPromoValidator(false)} />
        )}

        {/* Link Stripe Customer Modal */}
        {linkStripeClient && (
          <LinkStripeCustomerModal
            userId={linkStripeClient.userId}
            userName={
              linkStripeClient.clientName ||
              linkStripeClient.hotelName ||
              linkStripeClient.email
            }
            userEmail={linkStripeClient.email}
            currentStripeCustomerId={linkStripeClient.stripeCustomerId}
            onClose={() => setLinkStripeClient(null)}
            onSave={(data) => {
              linkStripeMutation.mutate(data, {
                onSuccess: () => setLinkStripeClient(null),
              });
            }}
            isPending={linkStripeMutation.isPending}
          />
        )}

        {/* Charge Preview Modal */}
        {showPreviewModal && previewClient && (
          <ChargePreviewModal
            client={previewClient}
            month={selectedMonth}
            onClose={() => {
              setShowPreviewModal(false);
              setPreviewClient(null);
              setIsLoadingPreview(false);
            }}
            onConfirm={handleConfirmChargeCard}
            onPreviewInvoiceEmail={(opts) => {
              setPreviewEmailOpts(opts);
              setShowInvoiceEmailPreviewModal(true);
            }}
            onCopyPayLink={() => copyInvoicePayLink(previewClient)}
            isLoading={
              chargeClientMutation.isPending ||
              scheduleInvoiceNoticeMutation.isPending
            }
          />
        )}

        {/* Invoice email preview */}
        {showInvoiceEmailPreviewModal && previewClient && (
          <InvoiceEmailPreviewModal
            client={previewClient}
            month={selectedMonth}
            collectionMethod={previewEmailOpts.collectionMethod}
            daysUntilDue={previewEmailOpts.daysUntilDue}
            onClose={() => setShowInvoiceEmailPreviewModal(false)}
          />
        )}
      </div>
      {/* Charge Again Confirmation Modal */}
      {rechargeClient && (
        <ConfirmModal
          title="Charge this client again?"
          actions={[
            { label: "Cancel", onClick: () => setRechargeClient(null) },
            {
              label: "Yes, Charge Again",
              onClick: handleConfirmChargeAgain,
              variant: "primary",
            },
          ]}
        >
          {displayNameOf(rechargeClient)} was already charged for this period.
          This is normally only needed after a refund. Continuing opens the
          charge preview so you can bill them again. The existing charge stays in
          place — it's only replaced if the new charge goes through.
        </ConfirmModal>
      )}

      {/* Void Invoice Confirmation Modal */}
      {voidInvoiceClient && (
        <ConfirmModal
          title="Void this invoice?"
          actions={[
            {
              label: "Cancel",
              onClick: () => setVoidInvoiceClient(null),
              disabled: voidInvoiceMutation.isPending,
            },
            {
              label: voidInvoiceMutation.isPending
                ? "Voiding..."
                : "Yes, Void Invoice",
              onClick: handleConfirmVoidInvoice,
              variant: "danger",
              disabled: voidInvoiceMutation.isPending,
            },
          ]}
        >
          This closes the Stripe payment page for{" "}
          {displayNameOf(voidInvoiceClient)}
          's open invoice — they will no longer be able to pay it — and frees
          the period so it can be billed again. Use it to correct a mistaken or
          outdated invoice.
        </ConfirmModal>
      )}

      {isLoadingPreview && (
        <div className="fixed inset-0 z-10 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-10 h-10 border-t-transparent border-b-transparent border-r-transparent border-l-primary border-4 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Retry Confirmation Modal */}
      {retryConfirmClient && (
        <ConfirmModal
          title={
            <>
              Retry the original invoice
              {retryConfirmClient.totalAmount != null
                ? ` of ${formatCurrencyPrecise(retryConfirmClient.totalAmount)}`
                : ""}
              ?
            </>
          }
          actions={[
            { label: "Cancel", onClick: () => setRetryConfirmClient(null) },
            {
              label: "Adjust",
              onClick: handleAdjustFromRetry,
              variant: "outline",
            },
            {
              label: "Retry",
              onClick: handleConfirmRetry,
              variant: "danger",
            },
          ]}
        >
          Retry always re-collects the original invoiced amount. To charge a
          different amount, use Adjust instead.
        </ConfirmModal>
      )}

      <BulkChargeModal
        stage={
          showBulkChargeConfirm ? "confirm" : bulkJobId ? "progress" : null
        }
        selectedCount={selectedIds.size}
        job={bulkJob}
        onCancel={() => setShowBulkChargeConfirm(false)}
        onConfirm={handleConfirmBulkCharge}
        onDone={() => setBulkJobId(null)}
      />

      {/* TOTP gate for charging */}
      {unlock.showUnlock && !unlock.showEnroll && (
        <ChargeTotpModal
          title="Unlock charging"
          subtitle="Enter a 6-digit code from your authenticator to unlock charging for 60 minutes."
          submitLabel="Unlock"
          onSubmit={(code) => unlock.submitUnlock(code)}
          onClose={unlock.closeUnlock}
          error={unlock.unlockError}
          isSubmitting={unlock.submitting}
        />
      )}
      {unlock.showEnroll && (
        <EnrollTotpModal
          onEnrolled={unlock.onEnrolled}
          onClose={unlock.cancelEnroll}
        />
      )}
      {unlock.blocked && (
        <ConfirmModal
          title="Can't charge"
          zIndexClass="z-[60]"
          actions={[
            { label: "OK", onClick: unlock.closeBlocked, variant: "primary" },
          ]}
        >
          {unlock.blocked}
        </ConfirmModal>
      )}
    </Page>
  );
}

export default Billing;
